import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Briefcase, History, Users, Target, Zap, LineChart, Bot,
  FlaskConical, Search, BarChart3, SearchCheck, Wallet, CreditCard, Bell,
  Settings, Building2, Database, MessageSquare, Landmark,
} from 'lucide-react';

const sections = {
  home: {
    label: 'Home',
    items: [
      { name: 'Dashboard', path: '/home/dashboard', icon: LayoutDashboard, end: true },
      { name: 'Portfolio', path: '/home/portfolio', icon: Briefcase, end: true },
      { name: 'History', path: '/home/history', icon: History, end: true },
      { name: 'Community', path: '/home/community', icon: Users, end: true },
    ],
  },
  strategies: {
    label: 'Strategies',
    items: [
      { name: 'Manager', path: '/strategies/manager', icon: Zap, end: true },
      { name: 'Trading Floor', path: '/strategies/trading-floor', icon: LineChart, end: false },
      { name: 'Builder', path: '/strategies/builder', icon: Target, end: true },
    ],
  },
  apollo: {
    label: 'Apollo',
    items: [
      { name: 'Brokers', path: '/apollo/brokers', icon: Bot, end: false },
      { name: 'Fund Manager', path: '/apollo/fund-manager', icon: Landmark, end: true },
      { name: 'Chat', path: '/apollo/chat', icon: MessageSquare, end: false },
    ],
  },
  analysis: {
    label: 'Analysis',
    items: [
      { name: 'Backtesting', path: '/analysis/backtesting', icon: FlaskConical, end: true },
      { name: 'Research', path: '/analysis/research', icon: Search, end: true },
      { name: 'Charts', path: '/analysis/charts', icon: BarChart3, end: true },
      { name: 'Sherlock', path: '/analysis/sherlock', icon: SearchCheck, end: true },
    ],
  },
  account: {
    label: 'Account',
    items: [
      { name: 'Portfolio', path: '/account/portfolio', icon: Wallet, end: true },
      { name: 'Billing', path: '/account/billing', icon: CreditCard, end: true },
      { name: 'Notifications', path: '/account/notifications', icon: Bell, end: true },
    ],
  },
  settings: {
    label: 'Settings',
    items: [
      { name: 'General', path: '/settings/general', icon: Settings, end: true },
      { name: 'Brokers', path: '/settings/brokers', icon: Building2, end: true },
      { name: 'Market Data', path: '/settings/market-data', icon: Database, end: true },
    ],
  },
};

const sidebarVariants = {
  open: {
    width: '16rem',
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  closed: {
    width: '5rem',
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
};

function SectionNav({ sectionData, isOpen, handleNavClick }) {
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-1.5"
          >
            {sectionData.label}
          </motion.p>
        )}
      </AnimatePresence>
      <ul className="space-y-1">
        {sectionData.items.map((item) => (
          <li key={item.path}>
            <NavLink
              to={item.path}
              end={item.end}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center ${isOpen ? 'space-x-3 px-4' : 'justify-center px-2'} py-2.5 rounded-lg transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-primary/15 text-primary border-l-2 border-primary'
                    : 'text-muted-foreground hover:bg-accent/10 hover:text-foreground'
                }`
              }
              title={!isOpen ? item.name : ''}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <AnimatePresence>
                {isOpen && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="font-medium text-sm whitespace-nowrap"
                  >
                    {item.name}
                  </motion.span>
                )}
              </AnimatePresence>
            </NavLink>
          </li>
        ))}
      </ul>
    </>
  );
}

function Sidebar({ isOpen, setIsOpen, onNavigate, showAllSections }) {
  const location = useLocation();
  const currentSection = location.pathname.split('/')[1];
  const sectionData = sections[currentSection];

  const handleNavClick = () => {
    if (onNavigate) onNavigate();
  };

  const sectionsToRender = showAllSections
    ? Object.values(sections)
    : sectionData ? [sectionData] : [];

  return (
    <motion.div
      initial="open"
      animate={isOpen ? 'open' : 'closed'}
      variants={sidebarVariants}
      className="bg-card/60 backdrop-blur-xl h-screen border-r border-white/10 flex flex-col select-none flex-shrink-0"
    >
      {/* Logo */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`h-14 border-b border-white/10 w-full cursor-pointer hover:bg-accent/10 transition-colors flex items-center flex-shrink-0 ${isOpen ? 'px-4 space-x-3' : 'justify-center'}`}
      >
        <div className="w-8 h-8 text-neon flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-full h-full">
            <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
            <ellipse cx="12" cy="12" rx="10" ry="3.5" />
            <ellipse cx="12" cy="12" rx="10" ry="3.5" transform="rotate(60 12 12)" />
            <ellipse cx="12" cy="12" rx="10" ry="3.5" transform="rotate(-60 12 12)" />
          </svg>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="text-base font-bold text-foreground neon-text"
            >
              Proton
            </motion.p>
          )}
        </AnimatePresence>
      </button>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto space-y-4">
        {sectionsToRender.map((sec) => (
          <SectionNav key={sec.label} sectionData={sec} isOpen={isOpen} handleNavClick={handleNavClick} />
        ))}
      </nav>
    </motion.div>
  );
}

export default Sidebar;
